import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, TrendingUp, Users, DollarSign, GraduationCap } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroStatTile } from "@/components/shared/PageHero";
import AdSlot from "@/components/shared/AdSlot";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import NoDegreeIntro from "@/components/content/NoDegreeIntro";
import { supabase } from "@/integrations/supabase/client";
import { formatLocationName } from "@/lib/utils";
import { 
  getStateBySlug, 
  isValidState, 
  getAreaCodeForLocation, 
  formatLocationForDisplay 
} from "@/lib/stateUtils";
import NotFound from "./NotFound";

const NoDegreeNavigator = () => {
  const { location } = useParams<{ location: string }>();
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  // Detect if this is entry-level route vs no-degree route
  const isEntryLevel = window.location.pathname.includes('/best-paying-entry-level/');
  const jobType = isEntryLevel ? 'entry_level' : 'no_degree';

  // Validate state and get proper area code
  const stateInfo = location ? getStateBySlug(location) : null;
  const areaCode = stateInfo?.code;
  const locationName = stateInfo?.name || location || '';
  const isInvalidLocation = Boolean(location && !stateInfo);

  const { data: occupationsData, isLoading } = useQuery({
    queryKey: ['occupations', areaCode, jobType, useRpp],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: {
          area: locationName,
          type: jobType,
          limit: 50,
          year: 2025,
          rpp: useRpp
        }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!areaCode && !isInvalidLocation,
    placeholderData: keepPreviousData
  });

  // Get real area statistics
  const { data: areaStats } = useQuery({
    queryKey: ['area-stats', areaCode],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-state-area-stats', {
        body: { area_code: areaCode }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!areaCode && !isInvalidLocation
  });

  if (isInvalidLocation) {
    return <NotFound />;
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const pageTitle = isEntryLevel 
    ? `Best Paying Entry-Level Jobs in ${locationName} - Start Your Career`
    : `Best No-Degree Jobs in ${locationName} - High Paying Careers Without College`;
  const pageDescription = isEntryLevel
    ? `Discover top entry-level job opportunities in ${locationName}. See salary data, job openings, and career advancement paths for new graduates and career changers.`
    : `Discover high-paying jobs that don't require a college degree in ${locationName}. See salary data, job openings, and entry-level opportunities for skilled trades and technical careers.`;

  // SeoHead must render even while data is loading: prerender validation
  // rejects any snapshot whose <title> is still the default SPA shell.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SeoHead
          title={pageTitle}
          description={pageDescription}
          occupation={isEntryLevel ? "Entry-Level Jobs" : "No-Degree Jobs"}
          location={locationName}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-32 bg-muted animate-pulse rounded" />
              <div className="h-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="space-y-4">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        occupation={isEntryLevel ? "Entry-Level Jobs" : "No-Degree Jobs"}
        location={locationName}
        salaryData={occupationsData?.occupations?.[0]}
      />

      {/* Hero Section */}
      <PageHero
        badge={{ icon: GraduationCap, label: isEntryLevel ? 'Entry-Level Opportunities' : 'No College Degree Required' }}
        title={isEntryLevel ? `Best Paying Entry-Level Jobs in ${locationName}` : `No-Degree Jobs in ${locationName}`}
        subtitle={isEntryLevel
          ? 'Launch your career with entry-level opportunities'
          : 'High-paying careers that don\'t require a college degree'}
      >
        {/* Top-level ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <AdSlot slot="leaderboard" className="max-w-4xl" />
        </AdFrame>

        {(areaStats || occupationsData?.occupations) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile
              icon={DollarSign}
              value={occupationsData?.occupations?.[0]?.annual_mean_wage
                ? formatCurrency(occupationsData.occupations[0].annual_mean_wage)
                : formatCurrency(areaStats?.no_degree_avg_salary || 0)}
              label="Top Salary"
            />
            <HeroStatTile icon={Users} value={occupationsData?.occupations?.length || 0} label="Job Types" />
            <HeroStatTile icon={TrendingUp} value={formatCurrency(areaStats?.mean_salary || 0)} label="All Jobs Avg." />
            <HeroStatTile icon={MapPin} value="State" label="Coverage" />
          </div>
        )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Introduction Content */}
            <NoDegreeIntro 
              locationName={locationName}
              isEntryLevel={isEntryLevel}
            />
            
            {/* Controls */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    {isEntryLevel ? 'Entry-Level Job Opportunities' : 'No-Degree Job Opportunities'}
                  </CardTitle>
                  <RppToggle 
                    enabled={useRpp}
                    onToggle={setUseRpp}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  {isEntryLevel 
                    ? 'Great starting positions for new graduates and career changers, with opportunities for growth and advancement.'
                    : 'These careers typically require a high school diploma or equivalent, with on-the-job training, apprenticeships, or certification programs instead of a 4-year college degree.'
                  }
                </p>
                
                {/* In-content ad */}
                <AdFrame className="my-6">
                  <AdSlot slot="in-content" />
                </AdFrame>
              </CardContent>
            </Card>

            {/* Results Table */}
            {occupationsData?.occupations && (
              <SalaryTable
                data={occupationsData.occupations}
                title={isEntryLevel ? `Entry-Level Jobs in ${locationName}` : `No-Degree Jobs in ${locationName}`}
                showAffiliateEvery={8}
                area={location || ''}
                className="shadow-sm"
              />
            )}

            {/* Bottom content ad */}
            <AdFrame className="mt-8">
              <AdSlot slot="in-content" />
            </AdFrame>

            {/* Related Information */}
            <Card>
              <CardHeader>
                <CardTitle>{isEntryLevel ? 'About Entry-Level Careers' : 'About No-Degree Careers'}</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <p>
                  {isEntryLevel 
                    ? 'Entry-level positions provide excellent opportunities to start your career and build valuable experience. These roles typically offer growth potential and advancement paths through:'
                    : 'Many high-paying careers don\'t require a traditional 4-year college degree. These jobs often offer competitive salaries, strong job security, and clear advancement paths through:'
                  }
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  {isEntryLevel ? (
                    <>
                      <li>Mentorship and professional development programs</li>
                      <li>Skills training and certification opportunities</li>
                      <li>Performance-based advancement</li>
                      <li>Cross-departmental exposure and experience</li>
                      <li>Leadership development pathways</li>
                    </>
                  ) : (
                    <>
                      <li>On-the-job training programs</li>
                      <li>Apprenticeships (often paid)</li>
                      <li>Professional certifications</li>
                      <li>Trade school programs (typically 6 months to 2 years)</li>
                      <li>Community college associate degrees</li>
                    </>
                  )}
                </ul>
                <Separator className="my-4" />
                <p>
                  {isEntryLevel
                    ? 'The jobs shown above represent excellent starting positions with salary ranges typically between $35,000 and $65,000, based on Bureau of Labor Statistics data.'
                    : 'The jobs shown above are filtered to include only occupations where the typical education requirement is high school or less, based on Bureau of Labor Statistics data.'
                  }
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Sidebar Ad */}
            <AdSlot slot="sidebar" />

            {/* Quick Navigation */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Explore More</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/calculators`)}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Salary Calculator
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(isEntryLevel ? `/no-degree-jobs/${location}` : `/best-paying-entry-level/${location}`)}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {isEntryLevel ? 'No-Degree Jobs' : 'Entry-Level Jobs'}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/trends`)}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Wage Trends
                </Button>
              </CardContent>
            </Card>

            {/* Mobile bottom ad */}
            <AdFrame className="lg:hidden">
              <AdSlot slot="mobile-banner" />
            </AdFrame>
          </div>
          </div>
        </div>
    </div>
  );
};

export default NoDegreeNavigator;